import type { FieldCodec } from 'v5/core/entity-values/values.types';
import { customFieldColumns } from './customFieldColumns';
import { findCustomFieldRowForRead } from './customFieldRows';
import { validateField, validation } from './validation';

const INCHES_PER_FOOT = 12;

/** Total inches ⇄ feet + inches (legacy `HeightInput`). */
export const heightInches = {
  split(totalInches: number): { feet: number; inches: number } {
    const feet = Math.floor(totalInches / INCHES_PER_FOOT);
    return { feet, inches: totalInches - feet * INCHES_PER_FOOT };
  },
  /** `null` only when both parts are absent; a lone part counts the other as 0. */
  join(feet: number | null, inches: number | null): number | null {
    if (feet === null && inches === null) return null;
    return (feet ?? 0) * INCHES_PER_FOOT + (inches ?? 0);
  },
  /** `5' 11"`; empty string for `null`. */
  format(totalInches: number | null): string {
    if (totalInches === null) return '';
    const { feet, inches } = heightInches.split(totalInches);
    return `${feet}' ${inches}"`;
  }
};

function isEmpty(value: number | null): boolean {
  return value === null;
}

/** HEIGHT custom field: total inches in `integer_value` (legacy `HEIGHT.jsx`). */
export const heightCodec: FieldCodec<'height'> = {
  decode(entity, spec, ctx) {
    const row = findCustomFieldRowForRead(entity, spec.definition.id, ctx);
    return row?.integer_value ?? null;
  },
  encode(value) {
    return {
      type: 'custom',
      columns: customFieldColumns({ integer_value: value })
    };
  },
  empty() {
    return null;
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, value =>
      value !== null && (!Number.isInteger(value) || value < 0)
        ? validation.invalid({ code: 'whole-inches' })
        : validation.valid()
    );
  }
};
